import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import filter, { resolveFilterLabelsToIds } from '../../versa/filter.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import record, { flatten, deepen } from '../../versa/record.js';
import { notify, ask } from '../../versa/msgbox.js';
import { splitIdsByCompany, resolvePrinterDeviceId } from '../../versa/misc.js';
import pdf from '../../versa/pdf.js';
import view from '../views/inputs.js';
import form from '../forms/inputs.js';
import inventory_form from '../forms/inputs-inventory.js';
import putaway_form from '../forms/inputs-putaway.js';
import buildCreateInvoicesForm from '../forms/create-invoices.js';
import print_labels_form from '../forms/inputs-labels.js';
import reverse_form from '../forms/inputs-reverse.js';
import print_complete_form from '../forms/inputs-print-complete-report.js';
import * as types from '../types.js';

const inputs = function()
{

	let inputs_crud = crud.create({
		async loadBaseData()
		{
			api.call(api.list_companies, this.queryAll()).then((data) => db.set('companies', data).catch(error => notify(error)));
			api.call(api.list_warehouses, this.queryAll()).then((data) => db.set('warehouses', data).catch(error => notify(error)));
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data).catch(error => notify(error)));
			api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			api.call(api.list_products, this.queryAll()).then((data) => db.set('products', data).catch(error => notify(error)));
			api.call(api.list_units, this.queryAll()).then((data) => db.set('units', data).catch(error => notify(error)));
			api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));
			api.call(api.list_devices, this.queryAll()).then((data) => db.set('devices', data).catch(error => notify(error)));
			api.call(api.list_manufacturers, this.queryAll()).then((data) => db.set('manufacturers', data).catch(() => {}));
		},
		async queryData(query)
		{
			return await api.call(api.list_inputs, query);
		},
		async loadRecord(id)
		{
			let _record = await api.call(api.select_input, ...JSON.parse(id));
			return flatten(_record, 'items');
		},
		async editFilter() {
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			filter.edit(this.applyFilter.bind(this), this.filter, {
				reloadLocations: async (formData) => {
					const resolved = await resolveFilterLabelsToIds(formData, this.fieldspecs);
					const parts = [];
					for (let i = 0; i < 10; i++) {
						const f = resolved[`field-${i}`];
						const v = resolved[`value-${i}`];
						if (f === 'company_id' && v) parts.push(`company_id*eq*${v}`);
						else if (f === 'warehouse_id' && v != null && v !== '') parts.push(`warehouse_id*eq*${v}`);
						else if (f === 'warehouse_company_id' && v) parts.push(`company_id*eq*${v}`);
					}
					if (parts.length === 0) return;
					const params = new URLSearchParams();
					params.append('filter', parts.join(','));
					params.append('sort', '4');
					params.append('limit', '0');
					const data = await api.call(api.list_locations, '?' + params.toString());
					if (data && Array.isArray(data)) db.set('locations', data);
				}
			});
		},
		async applyFilter(data) {
			this.filter = await resolveFilterLabelsToIds(data, this.fieldspecs);
			this.filterExplicitlyApplied = true;
			api.call(api.list_locations, await this.queryFilteredForLocations()).then((data) => db.set('locations', data)).catch(() => {});
			await this.loadPage(1);
		},
		async saveRecord(data, old_data)
		{
			data = deepen(data, 'items');
			let _record = {
				order_type: data.order_type,
				order_id: Number(data.order_id),
				warehouse_company_id: data.warehouse_company_id,
				warehouse_id: Number(data.warehouse_id),
				location_id: Number(data.location_id),
				program_id: (data.program_id != null && data.program_id !== '' && Number(data.program_id) >= 0) ? Number(data.program_id) : null,
				invoice_number: data.invoice_number,
				invoice_xml: data.invoice_xml,
				volumes: Number(data.volumes),
				weight: Number(data.weight),
				status: Number(data.status),
				items: data.items
				.filter(item => item && (item.product_id != null && item.product_id !== '') && Number(item.product_id) > 0)
				.map(item => {
					const pp = item.product_packaging_id;
					const packaging_id = (pp != null && pp !== '' && Number(pp) > 0) ? Number(pp) : null;
					return {
						product_id: Number(item.product_id),
						unit_id: Number(item.unit_id),
						quantity: Number(item.quantity),
						unit_price: Number(item.unit_price),
						product_packaging_id: packaging_id,
					};
				})
			};
			if (old_data)
				return await api.call(api.update_input, data.company_id, data.id, _record);
			else
				return await api.call(api.insert_input, data.company_id, _record);
		},
		async inventory()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				let _record = {
					warehouse_company_id: data.warehouse_company_id,
					warehouse_id: Number(data.warehouse_id),
					location_id: Number(data.location_id),
					program_id: (data.program_id != null && data.program_id !== '') ? Number(data.program_id) : null,
					lots: data.lots.map(lot => {
						const pp = lot.product_packaging_id;
						const packaging_id = (pp != null && pp !== '' && Number(pp) > 0) ? Number(pp) : null;
						const rawId = lot.id;
						const lotId = rawId != null && rawId !== '' ? Number(rawId) : null;
						return {
							id: Number.isFinite(lotId) && lotId > 0 ? lotId : null,
							product_id: Number(lot.product_id),
							product_packaging_id: packaging_id,
							lot_number: lot.lot_number,
							manufacture_date: lot.manufacture_date,
							expiration_date: lot.expiration_date,
							quantity: Number(lot.quantity),
							status: Number(lot.status),
							is_blocked: lot.is_blocked == '1'	// Can be: undefined, '0', '1'
						};
					})
				};
				if (await api.call(api.inventory_input, data.company_id, data.input_id, _record))
				{
					api.call(api.list_lots, this.queryAll()).then((data) => db.set('lots', data).catch(error => notify(error)));	// Get new lots
					this.loadPage();	// Show updated input status
					return true;
				}
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para conferência', 'Conferência de Entrada');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			// Alinhado a entities/input_.py: inventory() exige status >= ST_RECEIVED (30); >= ST_VERIFIED (50) já concluiu.
			const INPUT_ST_RECEIVED = 30;
			const INPUT_ST_VERIFIED = 50;
			const st = Number(input.status);
			if (st < INPUT_ST_RECEIVED)
				return notify(
					'Conclua o recebimento desta entrada antes da conferência (status "Recebida" ou superior, por exemplo após registrar data de recebimento na edição da entrada).',
					'Conferência de Entrada'
				);
			if (st >= INPUT_ST_VERIFIED)
				return notify('Esta entrada já foi conferida.', 'Conferência de Entrada');
			let lots = await api.call(api.list_lots, `?filter=company_id*eq*${input.company_id},input_id*eq*${input.id}&limit=0`);
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'location_id': input.location_id,
				'program_id': input.program_id ?? '',
				'lots': lots.map(lot => { lot.is_blocked = Boolean(lot.status & 0b1000); return lot; })
			};
			await api.call(api.list_programs, this.queryAll()).then((data) => db.set('programs', data).catch(() => {}));
			record.edit(inventory_form, sendRequest, flatten(_record, 'lots'));
		},
		async putAway()
		{
			const fmtExp = (d) =>
			{
				if (d == null || d === '') return '';
				const s = String(d);
				return s.length >= 10 ? s.slice(0, 10) : s;
			};
			/** Uma linha por lote na posição de recebimento; quantidades somadas se houver mais de um stock_id. */
			const buildPutawayRows = (stocks, input) =>
			{
				const stagingId = Number(input.location_id);
				let cand = (stocks || []).filter((s) =>
					Number(s.location_id) === stagingId && Number(s.quantity_available) > 0
				);
				if (cand.length === 0) {
					cand = (stocks || []).filter((s) => Number(s.quantity_available) > 0);
				}
				const byLot = new Map();
				for (const s of cand) {
					const lid = Number(s.lot_id);
					if (!byLot.has(lid)) {
						byLot.set(lid, {
							lot_id: lid,
							lot_number: s.lot_number || '',
							sku: s.sku || '',
							product_name: s.product_name || '',
							expiration_date: fmtExp(s.expiration_date),
							location_id: '',
							quantity: 0,
						});
					}
					const row = byLot.get(lid);
					row.quantity += Number(s.quantity_available || 0);
					if (!row.lot_number && s.lot_number) row.lot_number = s.lot_number;
					if (!row.sku && s.sku) row.sku = s.sku;
					if (!row.product_name && s.product_name) row.product_name = s.product_name;
					if (!row.expiration_date && s.expiration_date) row.expiration_date = fmtExp(s.expiration_date);
				}
				return [...byLot.values()];
			};
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'puts');
				const puts = (data.puts || []).filter((put) =>
					put &&
					put.lot_id != null && put.lot_id !== '' &&
					put.location_id != null && put.location_id !== '' &&
					Number(put.quantity) > 0
				).map((put) => ({
					lot_id: Number(put.lot_id),
					location_id: Number(put.location_id),
					quantity: Number(put.quantity),
				}));
				if (puts.length === 0) {
					notify('Informe ao menos uma linha com posição de destino e quantidade.', 'Endereçamento de Lotes');
					return false;
				}
				const _record = { puts };
				if (await api.call(api.putaway_input, data.company_id, data.input_id, _record))
				{
					this.loadPage();
					return true;
				}
			};
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para endereçamento', 'Endereçamento de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let stocks = await api.call(api.list_stocks_by_input, input.company_id, input.id);
			const putawayRows = buildPutawayRows(stocks, input);
			if (putawayRows.length === 0) {
				return notify(
					'Não há estoque disponível na posição de recebimento desta entrada. Conclua a conferência ou verifique a posição da entrada.',
					'Endereçamento de Lotes'
				);
			}
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'warehouse_company_id': input.warehouse_company_id,
				'warehouse_id': input.warehouse_id,
				'stocks': putawayRows,
			};
			record.edit(putaway_form, sendRequest, flatten(_record, 'stocks'));
		},
		printInputs()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Listagem de Entradas');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_inputs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Listagem de Entradas', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printInputSKUs()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Cruzamento de Nota Fiscal');
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_skus, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas na listagem?`, 'Listagem de Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printXML()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Espelho da Nota Fiscal');
			}
			const sendRequest = async () =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.print_input_xml, { 'ids': ids }).then(showResult);
			}
			ask(`Gerar espelho da nota de ${this.getCount()} entradas?`, 'Espelho da Nota Fiscal', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printPutAwayOrder()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Ordem de Endereçamento');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_putaway, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Ordem de Endereçamento', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		async printLabels()
		{
			const sendRequest = async (data) =>
			{
				data = deepen(data, 'lots');
				const device_id = resolvePrinterDeviceId(data.device_id, '#input-labels #device_id');
				if (device_id == null)
				{
					notify('Selecione a impressora na lista (clique na opção; só o texto no campo não basta).', 'Atenção');
					return false;
				}
				let _record = {
					device_company_id: data.device_company_id,
					device_id,
					return_zpl: !!data.use_local_agent,
					lots: data.lots.map(lot => ({ 
						lot_id: Number(lot.id),
						labels: Number(lot.labels)
					}))
				};
				const result = await api.call(api.print_input_labels, data.company_id, data.input_id, _record);
				if (result?.zpl) {
					try {
						await api.sendZplToAgent(result.zpl, result.printer, result.printer_key);
						notify('Etiquetas enviadas ao agente local.', 'Impressão');
					} catch (e) {
						notify(e?.message || String(e), 'Agente local');
						return false;
					}
				}
				return result;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para impressão', 'Etiquetas de Lotes');
			let input = await api.call(api.select_input, ids[0][0], ids[0][1]);
			let lots = await api.call(api.list_lots, `?filter=company_id*eq*${input.company_id},input_id*eq*${input.id}&limit=0`);
			let _record = {
				'company_id': input.company_id,
				'input_id': input.id,
				'lots': lots.map(lot => { lot.labels = 1; return lot; })
			};
			record.edit(print_labels_form, sendRequest, flatten(_record, 'lots'));
		},
		printCosts()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Relatório de Custos');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_costs, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório?`, 'Relatório de Custos', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printConference()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Conferência');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_conference, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório de conferência?`, 'Relatório de Conferência', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		printDetails()
		{
			const showResult = async (resp) =>
			{
				this.clearGridSelection();
				if (resp?.pdf)
					pdf(resp.pdf);
				else if (resp?.message)
					notify(resp.message, 'Entrada detalhada');
			}
			const sendRequest = async () =>
			{
				let ids = splitIdsByCompany(await this.getSelectedIds(['company_id', 'id']));
				let resp = await api.call(api.print_input_details, { 'ids': ids }).then(showResult);
			}
			ask(`Incluir ${this.getCount()} entradas no relatório detalhado?`, 'Entrada Detalhada', (answer) =>
			{
				if (answer == 'Y')
					sendRequest();
			});
		},
		createInvoices()
		{
			const showResult = (resp) =>
			{
				this.clearGridSelection();
				notify(`Notas criadas: ${resp?.count || 0}`, 'Resultado');
				fs.destroy();
			}
			const sendRequest = async (data) =>
			{
				let ids = await this.getSelectedIds(['company_id', 'id']);
				let resp = await api.call(api.create_invoices, Object.assign(data, { 'io_type': 'I', 'ids': ids })).then(showResult);
			}
			let fs = formset.create(buildCreateInvoicesForm(true), sendRequest);
			fs.init = (() =>
			{
				const n = this.getCount();
				document.querySelector(`${fs.form.selector} #count`).innerHTML = n;
				const noun = document.querySelector(`${fs.form.selector} #io-noun`);
				if (noun)
					noun.textContent = n === 1 ? 'entrada' : 'entradas';
				Object.getPrototypeOf(fs).init.call(fs);
			})();
		},
		async reverseInput()
		{
			const sendRequest = async (data) =>
			{
				let resp = await api.call(api.reverse_input, data.company_id, data.input_id, { status: Number(data.status) });
				if (resp)
					this.loadPage();	// Show updated status
				return resp;
			}
			let ids = await this.getSelectedIds(['company_id', 'id'])
			if (ids.length != 1)
				return notify('Selecione apenas uma entrada para estornar', 'Estorno de Entrada');
			record.edit(reverse_form, sendRequest, { company_id: ids[0][0], input_id: ids[0][1] });
		},
	});
	
	// Index = position in the backend's SELECT
	let fieldspecs = [
		{ index: 1, field: 'company_id', label: 'Empresa', types: [types.datalist()], placeholder: '', title: 'Empresa', store: 'companies', labels: ['nick'] },
		{ index: 2, field: 'id', label: 'Entrada', types: [types.number(0, 2)], placeholder: '0', title: 'Identificador da entrada' },
		{ index: 4, field: 'order_id', label: 'Pedido', types: [types.number(0, 2)], placeholder: '0', title: 'Número do pedido vinculado' },
		{ index: 3, field: 'order_type', label: 'Origem', types: [types.opt_order_type()], placeholder: '', title: 'Origem do pedido vinculado' },
		{ index: 10, field: 'invoice_number', label: 'Nota', types: [], placeholder: '', title: 'Número da nota fiscal' },
		{ index: 11, field: 'invoice_xml', label: 'XML', types: [], placeholder: '', title: 'XML da nota fiscal' },
		{ index: 5, field: 'created_on', label: 'Criação', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora da criação da entrada' },
		{ index: 6, field: 'received_on', label: 'Recebimento', types: [types.datetime()], placeholder: 'dd/mm/aaaa hh:mm:ss', title: 'Data e hora de recebimento da mercadoria' },
		{ index: 7, field: 'warehouse_company_id', label: 'Destinatária', types: [types.datalist()], placeholder: '', title: 'Empresa do armazém de recebimento', store: 'companies', labels: ['nick'] },
		{ index: 8, field: 'warehouse_id', label: 'Armazém', types: [types.datalist()], placeholder: '', title: 'Armazém de recebimento', store: 'warehouses', labels: ['name'] },
		{ index: 15, field: 'program_id', label: 'Programa', types: [types.datalist()], placeholder: '', title: 'Programa da entrada', store: 'programs', labels: ['name'] },
		{ index: 9, field: 'location_id', label: 'Posição', types: [types.datalist()], placeholder: '', title: 'Posição no armazém', store: 'locations', labels: ['address'], separator: '-' },
		{ index: 12, field: 'volumes', label: 'Volumes', types: [types.number(0, 2)], placeholder: '0', title: 'Número de volumes' },
		{ index: 13, field: 'weight', label: 'Peso', types: [types.number(3)], placeholder: '0,000', title: 'Peso total dos volumes' },
		{ index: 14, field: 'status', label: 'Status', types: [types.opt_input_status(), types.datalist()], placeholder: '', title: 'Status da entrada' },
		{ index: 19, field: 'lines_packaging_summary', label: 'Embalagem (itens)', types: [], placeholder: '', title: 'Resumo das embalagens informadas nas linhas da entrada' },
		{ index: 20, field: 'lines_manufacturer_summary', label: 'Fabricante (itens)', types: [], placeholder: '', title: 'Resumo dos fabricantes das embalagens nas linhas da entrada' },
		{ index: 21, field: 'lines_sku_summary', label: 'SKU (itens)', types: [], placeholder: 'trecho do SKU', title: 'Busca nos SKUs das linhas da entrada (operador Contém recomendado)' },
		{ index: 22, field: 'lines_product_summary', label: 'Produto (itens)', types: [], placeholder: 'nome do produto', title: 'Busca nos nomes dos produtos das linhas (operador Contém recomendado)' },
	];

	let operations = [
		{ hash: '#inventory-input',		label: 'Conferência&nbsp;de&nbsp;Entrada' 	},
		{ hash: '#submit-putaway',		label: 'Endereçamento&nbsp;de&nbsp;Lotes' 	},
		{ hash: '#create-invoices',		label: 'Gerar&nbsp;Notas&nbsp;Fiscais'		},
		{ hash: '#reverse-input',		label: 'Estornar&nbsp;Entrada'				},
	];

	let reports = [
		{ hash: '#print-inputs',		label: 'Listagem&nbsp;de&nbsp;Entradas' 			},
		{ hash: '#print-skus',			label: 'Cruzamento&nbsp;de&nbsp;Nota&nbsp;Fiscal'	},
		{ hash: '#print-xml', 			label: 'Espelho&nbsp;da&nbsp;Nota&nbsp;Fiscal' 		},
		{ hash: '#print-putaway', 		label: 'Ordem&nbsp;de&nbsp;Endereçamento' 			},
		{ hash: '#print-labels',		label: 'Etiquetas&nbsp;de&nbsp;Lotes' 				},
		{ hash: '#print-costs',			label: 'Relatório&nbsp;de&nbsp;Custos' 				},
		{ hash: '#print-conference',	label: 'Relatório&nbsp;de&nbsp;Conferência' 		},
		{ hash: '#print-details',		label: 'Entrada&nbsp;Detalhada' 					},
		{ hash: '#print-inputs-complete',	label: 'Relatório&nbsp;completo&nbsp;(status&nbsp;/&nbsp;NF)' },
	];

	router.set('#inventory-input',		inputs_crud.inventory.bind(inputs_crud));
	router.set('#submit-putaway', 		inputs_crud.putAway.bind(inputs_crud));
	router.set('#create-invoices', 		inputs_crud.createInvoices.bind(inputs_crud));
	router.set('#reverse-input', 		inputs_crud.reverseInput.bind(inputs_crud));

	router.set('#print-inputs', 		inputs_crud.printInputs.bind(inputs_crud));
	router.set('#print-skus', 			inputs_crud.printInputSKUs.bind(inputs_crud));
	router.set('#print-xml', 			inputs_crud.printXML.bind(inputs_crud));
	router.set('#print-putaway', 		inputs_crud.printPutAwayOrder.bind(inputs_crud));
	router.set('#print-labels', 		inputs_crud.printLabels.bind(inputs_crud));
	router.set('#print-costs', 			inputs_crud.printCosts.bind(inputs_crud));
	router.set('#print-conference', 	inputs_crud.printConference.bind(inputs_crud));
	router.set('#print-details', 		inputs_crud.printDetails.bind(inputs_crud));

	async function resolveCompanyIdForReport(raw) {
		if (!raw || String(raw).trim() === '') return '';
		const id = String(raw).trim();
		const c = await db.get('companies', id) || await db.get('companies', [id]);
		return c?.id ?? id;
	}

	async function buildInputsCompletePayload(formData) {
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		const o = {
			company_id,
			date_from: formData.date_from,
			date_to: formData.date_to,
			date_basis: formData.date_basis || 'created',
			format: (formData.format || 'pdf').toLowerCase(),
		};
		if (formData.warehouse_id) o.warehouse_id = Number(formData.warehouse_id);
		if (formData.status) o.status = Number(formData.status);
		if (formData.order_type) o.order_type = formData.order_type;
		return o;
	}

	async function reloadWarehousesCompleteModal(companyRaw) {
		const sel = document.querySelector('#inputs-print-complete-report #warehouse_id');
		if (!sel) return;
		sel.innerHTML = '<option value="">Todos</option>';
		const company_id = await resolveCompanyIdForReport(companyRaw);
		if (!company_id) return;
		const data = await api.call(api.list_warehouses, `?filter=company_id*eq*${company_id}&limit=0&sort=2`);
		if (!Array.isArray(data)) return;
		for (const w of data) {
			const opt = document.createElement('option');
			opt.value = String(w.id);
			opt.textContent = `${w.name ?? ''} (${w.id})`;
			sel.appendChild(opt);
		}
	}

	function downloadInputsCompleteFile(base64Content, filename, mimeType) {
		const bin = atob(base64Content);
		const arr = new Uint8Array(bin.length);
		for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
		const blob = new Blob([arr], { type: mimeType });
		const a = document.createElement('a');
		a.href = URL.createObjectURL(blob);
		a.download = filename || 'relatorio';
		a.click();
		URL.revokeObjectURL(a.href);
	}

	async function updateInputsCompleteCountMsg(getValues) {
		const el = document.querySelector('#ic-count-msg');
		if (!el) return;
		const formData = getValues?.() || {};
		if (!formData.date_from || !formData.date_to) {
			el.textContent = 'Informe as datas para estimar a quantidade de registros.';
			return;
		}
		const company_id = await resolveCompanyIdForReport(formData.company_id);
		if (!company_id) {
			el.textContent = 'Selecione a empresa para contar.';
			return;
		}
		const payload = await buildInputsCompletePayload(formData);
		delete payload.format;
		const resp = await api.call(api.count_inputs_complete_report, payload);
		const n = resp?.count;
		el.textContent = typeof n === 'number' ? `${n} entrada(s) no período com os filtros atuais.` : '—';
	}

	router.set('#print-inputs-complete', () => {
		const dlg = record.edit(print_complete_form, async (data) => {
			try {
				const payload = await buildInputsCompletePayload(data);
				const resp = await api.call(api.print_inputs_complete_report, payload);
				if (resp?.pdf) pdf(resp.pdf);
				else if (resp?.csv) {
					downloadInputsCompleteFile(resp.csv, resp.filename || 'entradas-completas.csv', 'text/csv;charset=utf-8');
					notify('Download iniciado.', 'Entradas');
				} else if (resp?.xlsx) {
					downloadInputsCompleteFile(resp.xlsx, resp.filename || 'entradas-completas.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
					notify('Download iniciado.', 'Entradas');
				} else notify(resp?.message || 'Nenhum dado para o relatório.', 'Entradas');
			} catch (e) {
				notify(e?.message || String(e), 'Erro');
			}
			dlg.destroy();
		}, {}, false);
		const formEl = document.querySelector(print_complete_form.form.selector);
		const refreshCount = () => updateInputsCompleteCountMsg(() => dlg.form?.getValues?.());
		if (formEl) {
			formEl.querySelector('#company_id')?.addEventListener('change', async () => {
				await reloadWarehousesCompleteModal(formEl.querySelector('#company_id')?.value);
				refreshCount();
			});
			formEl.addEventListener('change', refreshCount);
			formEl.addEventListener('input', refreshCount);
		}
		setTimeout(refreshCount, 300);
	});

	inputs_crud.run(
		'inputs',
		'Entradas',
		document.getElementById('workarea'),
		view,
		form,
		fieldspecs,
		operations,
		reports,
		{ default_columns: '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,19,20,21,22' }
	);

};

export default inputs;

